import { combineQueries, createEntityQuery, Order } from '@datorama/akita';
import { parseISO } from 'date-fns';
import { groupBy, isEqual, omit, uniqWith } from 'lodash-es';
import { distinctUntilChanged, map, type Observable } from 'rxjs';
import { businessActivitiesStore, responsesStore } from './store';
import { nestCriteria } from './utils';
import { activeOrganisationId$ } from 'context/OrganisationContext/query';
import { context$ as periodContext$ } from 'context/PeriodContext/query';
import { entities$ as alignmentResults$ } from 'state/TaxonomyAlignmentResults/query';
import { entities$ as contributions$ } from 'state/TaxonomyContributions/query';
import { entities$ as objectives$ } from 'state/TaxonomyObjectives/query';
import {
   type Organisation,
   type Period,
   type TaxonomyActivity,
   type TaxonomyAlignmentResult,
   type TaxonomyBusinessActivity,
   type TaxonomyBusinessActivity_ActivityAssignment,
   type TaxonomyContribution,
   type TaxonomyCriterionResponse,
   type TaxonomyObjective,
} from 'types/models';
import { TaxonomyContributionType } from 'utils/enum';

export const query = createEntityQuery(businessActivitiesStore, { sortBy: 'updatedAt', sortByOrder: Order.DESC });

export const activeOrganisationAssignmentId$ = query.select('activeOrganisationAssignment');

export const responsesQuery = createEntityQuery(responsesStore);

export const assignedTaxonomyActivities$ = query.selectActive(({ taxonomyActivityAssignments }) => taxonomyActivityAssignments) as Observable<
   TaxonomyBusinessActivity_ActivityAssignment[] | undefined
>;
export const suggestedTaxonomyActivities$ = query.selectActive(({ suggestedtaxonomyactivities }) => suggestedtaxonomyactivities);

export const allResponseEntities$ = responsesQuery.selectAll();

export const entities$ = query.selectAll();

export const loading$ = query.selectLoading();

export const activeEntity$ = combineQueries([
   query.selectActive() as Observable<TaxonomyBusinessActivity | undefined>,
   allResponseEntities$,
   periodContext$,
   activeOrganisationId$,
   alignmentResults$,
   contributions$,
   activeOrganisationAssignmentId$,
]).pipe(
   map(
      ([activeBusinessActivity, responses, periodContext, activeOrganisationId, alignmentResults, contributions, orgAssignmentId]: [
         TaxonomyBusinessActivity | undefined,
         TaxonomyCriterionResponse[],
         Period | undefined,
         Organisation['id'] | undefined,
         TaxonomyAlignmentResult[],
         TaxonomyContribution[],
         number | undefined,
      ]) =>
         activeBusinessActivity
            ? ({
                 ...omit(activeBusinessActivity, ['alignmentresults']),
                 ...omit(
                    (activeBusinessActivity?.organisationAssignments ?? []).find((orgAssignment) =>
                       orgAssignmentId ? orgAssignment.id === orgAssignmentId : orgAssignment?.organisation?.id === activeOrganisationId
                    ),
                    ['id']
                 ),
                 taxonomyActivityAssignments: Array.isArray(activeBusinessActivity?.taxonomyActivityAssignments)
                    ? activeBusinessActivity.taxonomyActivityAssignments
                         .filter(
                            (taxActivityAssignment) =>
                               taxActivityAssignment?.organisation?.id === activeOrganisationId &&
                               taxActivityAssignment?.validFrom &&
                               periodContext?.from &&
                               parseISO(taxActivityAssignment?.validFrom) >= parseISO(periodContext?.from) &&
                               taxActivityAssignment?.validTo &&
                               periodContext?.to &&
                               parseISO(taxActivityAssignment?.validTo) <= parseISO(periodContext?.to)
                         )
                         .map((taxActivityAssignment) => ({
                            ...taxActivityAssignment,
                            answerableCriteriaCount: Array.isArray(taxActivityAssignment?.taxonomyActivity?.criteria)
                               ? taxActivityAssignment?.taxonomyActivity?.criteria.filter(
                                    (criterion) => criterion?.activity?.id === taxActivityAssignment.taxonomyActivity?.id && criterion?.answerable
                                 ).length
                               : 0,
                            answeredCriteriaCount: Array.isArray(taxActivityAssignment?.taxonomyActivity?.criteria)
                               ? taxActivityAssignment?.taxonomyActivity?.criteria.filter(
                                    (criterion) =>
                                       criterion?.activity?.id === taxActivityAssignment.taxonomyActivity?.id &&
                                       criterion?.answerable &&
                                       responses.some(
                                          (response) =>
                                             response?.criterion?.id === criterion?.id &&
                                             response?.businessActivity?.id === activeBusinessActivity?.id &&
                                             response?.organisation?.id === activeOrganisationId &&
                                             response?.validFrom &&
                                             periodContext?.from &&
                                             parseISO(response?.validFrom) >= parseISO(periodContext?.from) &&
                                             response?.validTo &&
                                             periodContext?.to &&
                                             parseISO(response?.validTo) <= parseISO(periodContext?.to) &&
                                             !!response?.explanation
                                       )
                                 ).length
                               : 0,
                         }))
                    : [],
                 alignmentResult:
                    activeBusinessActivity?.alignmentresults?.find(
                       (ar: TaxonomyAlignmentResult) =>
                          ar?.businessActivity?.id === activeBusinessActivity?.id &&
                          ar?.organisation?.id === activeOrganisationId &&
                          ar?.validFrom &&
                          periodContext?.from &&
                          parseISO(ar?.validFrom) >= parseISO(periodContext?.from) &&
                          ar?.validTo &&
                          periodContext?.to &&
                          parseISO(ar?.validTo) <= parseISO(periodContext?.to)
                    ) ??
                    alignmentResults.find(
                       (ar) =>
                          ar?.businessActivity?.id === activeBusinessActivity?.id &&
                          ar?.organisation?.id === activeOrganisationId &&
                          ar?.validFrom &&
                          periodContext?.from &&
                          parseISO(ar?.validFrom) >= parseISO(periodContext?.from) &&
                          ar?.validTo &&
                          periodContext?.to &&
                          parseISO(ar?.validTo) <= parseISO(periodContext?.to)
                    ),
                 contributions: Array.isArray(activeBusinessActivity?.contributions)
                    ? activeBusinessActivity.contributions
                    : contributions.filter(
                         (c) =>
                            c?.businessActivity?.id === activeBusinessActivity?.id &&
                            c?.organisation?.id === activeOrganisationId &&
                            c?.validFrom &&
                            periodContext?.from &&
                            parseISO(c?.validFrom) >= parseISO(periodContext?.from) &&
                            c?.validTo &&
                            periodContext?.to &&
                            parseISO(c?.validTo) <= parseISO(periodContext?.to)
                      ),
              } as any)
            : undefined
   )
);

export const responses$ = combineQueries([
   allResponseEntities$,
   objectives$,
   assignedTaxonomyActivities$,
   activeEntity$,
   periodContext$,
   activeOrganisationId$,
]).pipe(
   map(([responses, objectives, assignedTaxonomyActivities, activeBusinessActivity, periodContext, activeOrganisationId]) =>
      responses && objectives && assignedTaxonomyActivities && activeBusinessActivity
         ? objectives.map((objective) => ({
              ...objective,
              taxonomyActivityAssignments: assignedTaxonomyActivities
                 ? assignedTaxonomyActivities.map((relation) => ({
                      ...relation,
                      taxonomyActivity: {
                         ...relation.taxonomyActivity,
                         ...(objective.status === TaxonomyContributionType.DO_NO_SIGNIFICANT_HARM
                            ? {
                                 criteriaByObjective: groupBy(
                                    nestCriteria(
                                       Array.isArray(relation?.taxonomyActivity?.criteria)
                                          ? uniqWith(
                                               relation.taxonomyActivity.criteria.filter(
                                                  (criterion) =>
                                                     criterion?.activity?.id === relation.taxonomyActivity?.id &&
                                                     ((criterion?.objective?.id === objective?.id &&
                                                        criterion.contributionType.code !== TaxonomyContributionType.DO_NO_SIGNIFICANT_HARM) ||
                                                        (criterion?.contributionType?.code === TaxonomyContributionType.DO_NO_SIGNIFICANT_HARM &&
                                                           objective?.id === 0 &&
                                                           !(
                                                              Array.isArray(activeBusinessActivity.objectives) &&
                                                              activeBusinessActivity.objectives.length === 1 &&
                                                              activeBusinessActivity.objectives.find(
                                                                 (anObjective: TaxonomyObjective) => anObjective.id === criterion?.objective?.id
                                                              )
                                                           )))
                                               ),
                                               (a, b) =>
                                                  a.desc === b.desc &&
                                                  a.key === b.key &&
                                                  a.answerable === b.answerable &&
                                                  a.level === b.level &&
                                                  a.objective?.id === b.objective?.id &&
                                                  a.answerable === true // need to do that so that we don't filter 2 criteria, where the children are distinct, but the parents are equal. In that case we would not show the children
                                            ).map((criterion) => ({
                                               ...criterion,
                                               response: responses.find(
                                                  (response) =>
                                                     response?.criterion?.id === criterion?.id &&
                                                     response?.businessActivity?.id === activeBusinessActivity?.id &&
                                                     response?.organisation?.id === activeOrganisationId &&
                                                     response?.validFrom &&
                                                     periodContext?.from &&
                                                     parseISO(response?.validFrom) >= parseISO(periodContext?.from) &&
                                                     response?.validTo &&
                                                     periodContext?.to &&
                                                     parseISO(response?.validTo) <= parseISO(periodContext?.to)
                                               ),
                                            }))
                                          : []
                                    ),
                                    (c) => c.objective.id
                                 ),
                              }
                            : {
                                 criteria: Array.isArray(relation?.taxonomyActivity?.criteria)
                                    ? nestCriteria(
                                         relation.taxonomyActivity.criteria
                                            .filter(
                                               (criterion) =>
                                                  criterion?.activity?.id === relation.taxonomyActivity?.id &&
                                                  ((criterion?.objective?.id === objective?.id &&
                                                     criterion.contributionType.code !== TaxonomyContributionType.DO_NO_SIGNIFICANT_HARM) ||
                                                     (criterion?.contributionType?.code === TaxonomyContributionType.DO_NO_SIGNIFICANT_HARM &&
                                                        objective?.id === 0 &&
                                                        !(
                                                           Array.isArray(activeBusinessActivity.objectives) &&
                                                           activeBusinessActivity.objectives.length === 1 &&
                                                           activeBusinessActivity.objectives.find(
                                                              (anObjective: TaxonomyObjective) => anObjective.id === criterion?.objective?.id
                                                           )
                                                        )))
                                            )
                                            .map((criterion) => ({
                                               ...criterion,
                                               response: responses.find(
                                                  (response) =>
                                                     response?.criterion?.id === criterion?.id &&
                                                     response?.businessActivity?.id === activeBusinessActivity?.id &&
                                                     response?.organisation?.id === activeOrganisationId &&
                                                     response?.validFrom &&
                                                     periodContext?.from &&
                                                     parseISO(response?.validFrom) >= parseISO(periodContext?.from) &&
                                                     response?.validTo &&
                                                     periodContext?.to &&
                                                     parseISO(response?.validTo) <= parseISO(periodContext?.to)
                                               ),
                                            }))
                                      )
                                    : [],
                              }),
                      },
                   }))
                 : null,
           }))
         : []
   ),
   distinctUntilChanged(isEqual)
);

export const someOpen$ = activeEntity$.pipe(
   map((activeBusinessActivity) => activeBusinessActivity?.alignmentResult?.type?.code === 'OP'),
   distinctUntilChanged()
);

export const unassignedEntities$ = combineQueries([entities$, activeOrganisationId$]).pipe(
   map(([businessActivities, activeOrganisationId]) =>
      businessActivities && activeOrganisationId
         ? businessActivities.filter((businessActivity) =>
              Array.isArray(businessActivity?.organisationAssignments)
                 ? !businessActivity.organisationAssignments.some((orgAssignment) => orgAssignment.organisation?.id === activeOrganisationId)
                 : true
           )
         : []
   ),
   distinctUntilChanged(isEqual)
);

export const allResponses$ = combineQueries([query.selectActiveId(), allResponseEntities$, activeOrganisationId$, periodContext$]).pipe(
   map(([activeBusinessActivityId, responses, activeOrganisationId, periodContext]) =>
      responses.filter(
         (response) =>
            response.organisation.id === activeOrganisationId &&
            response.validFrom === periodContext?.from &&
            response.validTo === periodContext?.to &&
            response.businessActivity?.id === Number.parseInt(activeBusinessActivityId, 10)
      )
   )
);

export const usedTaxonomyActivities$ = query.select('assignedTaxonomyActivities');
export const unassignedBusinessActivities$ = query.select('unassignedBusinessActivities');
export const businessActivitiesInScope$ = query.select('businessActivitiesInScope');
export const businessActivitiesOutOfScope$ = query.select('businessActivitiesOutOfScope');

export const activeCriterionId$ = query.select('activeCriterion');

export const statistics$ = query.select('statistics');

function getCriteriaProgress(
   objectiveId: number,
   activeOrganisationId: number,
   from: Date,
   to: Date,
   activeBusinessActivity: TaxonomyBusinessActivity,
   activeTaxonomyActivity: TaxonomyActivity,
   allResponses: TaxonomyCriterionResponse[]
) {
   const answerableCriteriaCount = (activeTaxonomyActivity?.criteria ?? []).filter(
      (criterion) =>
         criterion?.answerable &&
         (objectiveId === 0
            ? criterion?.contributionType?.code === TaxonomyContributionType.DO_NO_SIGNIFICANT_HARM &&
              (activeBusinessActivity?.objectives?.length === 1 ? criterion?.objective?.id !== activeBusinessActivity.objectives[0].id : true)
            : criterion?.contributionType?.code !== TaxonomyContributionType.DO_NO_SIGNIFICANT_HARM && criterion?.objective?.id === objectiveId)
   ).length;
   const answeredCriteriaCount = (activeTaxonomyActivity?.criteria ?? []).filter(
      (criterion) =>
         criterion?.answerable &&
         (objectiveId === 0
            ? criterion?.contributionType?.code === TaxonomyContributionType.DO_NO_SIGNIFICANT_HARM &&
              (activeBusinessActivity?.objectives?.length === 1 ? criterion?.objective?.id !== activeBusinessActivity.objectives[0].id : true)
            : criterion?.contributionType?.code !== TaxonomyContributionType.DO_NO_SIGNIFICANT_HARM && criterion?.objective?.id === objectiveId) &&
         allResponses.some(
            (response) =>
               from &&
               to &&
               response?.criterion?.id === criterion?.id &&
               response?.businessActivity?.id === activeBusinessActivity?.id &&
               response?.organisation?.id === activeOrganisationId &&
               parseISO(response?.validFrom) >= from &&
               parseISO(response?.validTo) <= to
         )
   ).length;
   const answeredWithNoCriteriaCount = (activeTaxonomyActivity?.criteria ?? []).filter(
      (criterion) =>
         criterion?.answerable &&
         (objectiveId === 0
            ? criterion?.contributionType?.code === TaxonomyContributionType.DO_NO_SIGNIFICANT_HARM &&
              (activeBusinessActivity?.objectives?.length === 1 ? criterion?.objective?.id !== activeBusinessActivity.objectives[0].id : true)
            : criterion?.contributionType?.code !== TaxonomyContributionType.DO_NO_SIGNIFICANT_HARM && criterion?.objective?.id === objectiveId) &&
         allResponses.some(
            (response) =>
               from &&
               to &&
               response?.criterion?.id === criterion?.id &&
               response?.businessActivity?.id === activeBusinessActivity?.id &&
               response?.organisation?.id === activeOrganisationId &&
               parseISO(response?.validFrom) >= from &&
               parseISO(response?.validTo) <= to &&
               response?.type?.code === 'NO'
         )
   ).length;

   return {
      answered: answeredCriteriaCount,
      no: answeredWithNoCriteriaCount,
      unAnswered: answerableCriteriaCount - answeredCriteriaCount,
      percentage: (answerableCriteriaCount === 0 ? 1 : answeredCriteriaCount / answerableCriteriaCount) * 100,
   };
}

export const progress$ = combineQueries([query.selectActive(), allResponses$, periodContext$, activeOrganisationId$, objectives$]).pipe(
   map(([activeBusinessActivity, allResponses = [], periodContext, activeOrganisationId, objectives = []]) => {
      if (!activeBusinessActivity || !periodContext || !activeOrganisationId) {
         return {
            answered: 0,
            no: 0,
            unAnswered: 0,
            percentage: 0,
         };
      }

      const activeTaxonomyActivityAssignment = (activeBusinessActivity as TaxonomyBusinessActivity).taxonomyActivityAssignments.find(
         (taxActivityAssignment) =>
            taxActivityAssignment?.organisation?.id === activeOrganisationId &&
            taxActivityAssignment?.validFrom &&
            periodContext?.from &&
            parseISO(taxActivityAssignment?.validFrom) >= parseISO(periodContext?.from) &&
            taxActivityAssignment?.validTo &&
            periodContext?.to &&
            parseISO(taxActivityAssignment?.validTo) <= parseISO(periodContext?.to)
      );

      if (!activeTaxonomyActivityAssignment?.taxonomyActivity) {
         return {
            answered: 0,
            no: 0,
            unAnswered: 0,
            percentage: 0,
         };
      }

      return Object.fromEntries(
         objectives.map(({ id: objectiveId }) => [
            objectiveId,
            getCriteriaProgress(
               objectiveId,
               activeOrganisationId,
               parseISO(periodContext.from),
               parseISO(periodContext.to),
               activeBusinessActivity as TaxonomyBusinessActivity,
               activeTaxonomyActivityAssignment.taxonomyActivity as TaxonomyActivity,
               allResponses
            ),
         ])
      );
   })
);
